
import { useState, useRef } from "react";
import { X, Upload, FileSpreadsheet, Loader2, AlertCircle, CheckCircle2 } from "lucide-react";
import { ParticipanteForm } from "@/types/participante";
import { importarConfirmados } from "@/lib/participantes";
import { apenasDigitos, formatarCPF as formatarCPFPreview, formatarTelefone as formatarTelPreview } from "@/lib/masks";

interface Preview {
  validos: ParticipanteForm[];
  invalidos: { linha: number; motivo: string }[];
}

interface Props {
  edicaoId: string;
  onClose: () => void;
  onImportado: () => void;
}

export default function ImportarExcelModal({ edicaoId, onClose, onImportado }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [nomeArquivo, setNomeArquivo] = useState("");
  const [processando, setProcessando] = useState(false);
  const [importando, setImportando] = useState(false);
  const [erro, setErro] = useState("");

  const processarArquivo = async (file: File) => {
    setProcessando(true);
    setErro("");
    setPreview(null);
    setNomeArquivo(file.name);

    try {
      const XLSX = await import("xlsx");
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const rows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, {
        defval: "",
      });

      if (rows.length === 0) {
        setErro("A planilha está vazia ou sem dados reconhecidos.");
        return;
      }

      // Mapeia colunas por prioridade: testa cada candidato na ordem e retorna
      // a primeira coluna encontrada na planilha (case-insensitive).
      const colunas = Object.keys(rows[0]);
      const achar = (candidatos: string[]) => {
        for (const k of candidatos) {
          const col = colunas.find((c) => c.trim().toLowerCase() === k.toLowerCase());
          if (col) return col;
        }
        return "";
      };

      const colNome = achar(["Nome cliente Site", "Nome", "nome completo", "nomeCompleto"]);
      const colCpf  = achar(["CPF cliente Site", "CPF", "cpf"]);
      const colEmail = achar(["E-mail", "Email", "email", "e-mail"]);
      const colWpp  = achar(["Celular", "WhatsApp", "whatsapp", "telefone", "fone", "cel", "celular"]);

      const faltando = [
        !colNome && "Nome",
        !colCpf  && "CPF",
        !colWpp  && "WhatsApp/Celular",
      ].filter(Boolean);

      if (faltando.length > 0) {
        setErro(
          `Coluna(s) não encontrada(s): ${faltando.join(", ")}.\n` +
          `Colunas detectadas na planilha: ${colunas.join(" · ")}`
        );
        return;
      }

      const validos: ParticipanteForm[] = [];
      const invalidos: { linha: number; motivo: string }[] = [];
      const cpfsVistos = new Set<string>();

      rows.forEach((row, idx) => {
        const linha = idx + 2;
        const nomeRaw = String(row[colNome] ?? "").trim();
        const nome    = nomeRaw.replace(/[0-9]/g, ''); // Remove números do nome
        const cpfRaw  = String(row[colCpf]  ?? "").trim();
        const email   = colEmail ? String(row[colEmail] ?? "").trim() : "";
        const whatsapp = apenasDigitos(String(row[colWpp] ?? "").trim());
        const cpf     = apenasDigitos(cpfRaw);

        if (!nome) {
          invalidos.push({ linha, motivo: "Nome em branco" });
          return;
        }
        if (/[0-9]/.test(nomeRaw)) {
          invalidos.push({ linha, motivo: "Nome contém números" });
          return;
        }
        if (cpf.length !== 11) {
          invalidos.push({ linha, motivo: `CPF inválido (${cpfRaw || "vazio"})` });
          return;
        }
        if (!whatsapp) {
          invalidos.push({ linha, motivo: "Celular em branco" });
          return;
        }
        if (cpfsVistos.has(cpf)) {
          invalidos.push({ linha, motivo: "CPF duplicado (ingresso extra)" });
          return;
        }

        cpfsVistos.add(cpf);
        validos.push({ nomeCompleto: nome, cpf, email: email || undefined, whatsapp });
      });

      setPreview({ validos, invalidos });
    } catch (err) {
      const detalhe = err instanceof Error ? ` (${err.message})` : "";
      setErro(`Não foi possível ler o arquivo. Verifique se é um .xlsx válido.${detalhe}`);
    } finally {
      setProcessando(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processarArquivo(file);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) processarArquivo(file);
  };

  const handleImportar = async () => {
    if (!preview || preview.validos.length === 0) return;
    setImportando(true);
    try {
      await importarConfirmados(edicaoId, preview.validos);
      onImportado();
    } catch {
      setErro("Erro ao importar. Tente novamente.");
    } finally {
      setImportando(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-black/5">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-black/40" />
            <h2 className="text-base font-semibold text-black">Importar Excel</h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-black/30 hover:text-black hover:bg-black/5 transition"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto px-6 py-5 space-y-5">
          {/* Área de upload */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => inputRef.current?.click()}
            className="border-2 border-dashed border-black/10 rounded-2xl p-8 flex flex-col items-center gap-3 cursor-pointer hover:border-black/20 hover:bg-black/[0.01] transition text-center"
          >
            <div className="w-10 h-10 bg-black/5 rounded-xl flex items-center justify-center">
              <Upload className="w-5 h-5 text-black/30" />
            </div>
            {nomeArquivo ? (
              <p className="text-sm font-medium text-black">{nomeArquivo}</p>
            ) : (
              <>
                <p className="text-sm font-medium text-black/60">
                  Clique ou arraste o arquivo aqui
                </p>
                <p className="text-xs text-black/30">Suporta .xlsx e .xls</p>
              </>
            )}
            <input
              ref={inputRef}
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={handleFileChange}
            />
          </div>

          {/* Colunas esperadas */}
          <div className="bg-black/[0.02] rounded-xl px-4 py-3 text-xs text-black/40 space-y-1.5">
            <p className="font-medium text-black/50">Colunas reconhecidas na planilha:</p>
            <p>
              <span className="text-black/50">Nome:</span>{" "}
              <span className="font-mono bg-black/5 px-1 rounded">Nome cliente Site</span>{" "}
              ou <span className="font-mono bg-black/5 px-1 rounded">Nome</span>
            </p>
            <p>
              <span className="text-black/50">CPF:</span>{" "}
              <span className="font-mono bg-black/5 px-1 rounded">CPF cliente Site</span>{" "}
              ou <span className="font-mono bg-black/5 px-1 rounded">CPF</span>
            </p>
            <p>
              <span className="text-black/50">E-mail (opcional):</span>{" "}
              <span className="font-mono bg-black/5 px-1 rounded">E-mail</span>{" "}
              ou <span className="font-mono bg-black/5 px-1 rounded">Email</span>
            </p>
            <p>
              <span className="text-black/50">WhatsApp:</span>{" "}
              <span className="font-mono bg-black/5 px-1 rounded">Celular</span>{" "}
              ou <span className="font-mono bg-black/5 px-1 rounded">WhatsApp</span>{" "}
              ou <span className="font-mono bg-black/5 px-1 rounded">Telefone</span>
            </p>
          </div>

          {/* Loading */}
          {processando && (
            <div className="flex items-center gap-2 text-sm text-black/40">
              <Loader2 className="w-4 h-4 animate-spin" />
              Processando planilha...
            </div>
          )}

          {/* Erro */}
          {erro && (
            <div className="flex items-start gap-2 text-sm text-red-500 bg-red-50 border border-red-100 px-4 py-3 rounded-xl">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {erro}
            </div>
          )}

          {/* Preview */}
          {preview && (
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 text-sm text-green-700 bg-green-50 px-3 py-1.5 rounded-lg">
                  <CheckCircle2 className="w-4 h-4" />
                  {preview.validos.length} válido{preview.validos.length !== 1 ? "s" : ""}
                </div>
                {preview.invalidos.length > 0 && (
                  <div className="flex items-center gap-1.5 text-sm text-red-600 bg-red-50 px-3 py-1.5 rounded-lg">
                    <AlertCircle className="w-4 h-4" />
                    {preview.invalidos.length} com problema
                  </div>
                )}
              </div>

              {/* Linhas inválidas */}
              {preview.invalidos.length > 0 && (
                <div className="bg-red-50 border border-red-100 rounded-xl px-4 py-3 space-y-1 max-h-32 overflow-y-auto">
                  <p className="text-xs font-medium text-red-600 mb-2">Linhas ignoradas:</p>
                  {preview.invalidos.map((inv) => (
                    <p key={inv.linha} className="text-xs text-red-500">
                      Linha {inv.linha}: {inv.motivo}
                    </p>
                  ))}
                </div>
              )}

              {/* Preview dos válidos */}
              {preview.validos.length > 0 && (
                <div className="bg-white border border-black/5 rounded-xl overflow-hidden max-h-52 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-white border-b border-black/5">
                      <tr>
                        <th className="text-left px-4 py-2.5 text-black/40 font-medium">Nome</th>
                        <th className="text-left px-4 py-2.5 text-black/40 font-medium">CPF</th>
                        <th className="text-left px-4 py-2.5 text-black/40 font-medium">WhatsApp</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-black/5">
                      {preview.validos.map((p, i) => (
                        <tr key={i} className="hover:bg-black/[0.01]">
                          <td className="px-4 py-2.5 text-black font-medium">{p.nomeCompleto}</td>
                          <td className="px-4 py-2.5 text-black/50 font-mono">
                            {formatarCPFPreview(p.cpf)}
                          </td>
                          <td className="px-4 py-2.5 text-black/50">{formatarTelPreview(p.whatsapp)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-4 border-t border-black/5">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-black/50 hover:text-black hover:bg-black/5 transition"
          >
            Cancelar
          </button>
          <button
            onClick={handleImportar}
            disabled={!preview || preview.validos.length === 0 || importando}
            className="px-5 py-2.5 bg-black text-white text-sm font-medium rounded-xl hover:bg-black/80 transition disabled:opacity-40 flex items-center gap-2"
          >
            {importando ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Importando...
              </>
            ) : (
              `Importar ${preview ? preview.validos.length : 0} registro${preview?.validos.length !== 1 ? "s" : ""}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
